import type { Practitioner, Reference } from '@medplum/fhirtypes'
import { formatDateTime } from '@seen/core/dates'
import { referenceDisplay } from '@seen/core/display'
import {
  countChangedFields,
  flattenChanges,
  type ResourceChangeSet,
  type ResourceDecision,
} from '@seen/core/queued-edits/model'
import { Badge, Button, cn } from '@seen/ui'
import {
  IconAlertTriangle,
  IconCheck,
  IconChevronDown,
  IconCode,
  IconSparkles,
  IconX,
} from '@tabler/icons-react'
import { type JSX, useState } from 'react'
import { Avatar } from '#src/components/core/Avatar/Avatar.tsx'
import { HoverPractitioner } from '#src/components/core/hover-card/HoverPractitioner.tsx'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { DiffTable } from '#src/components/queued-edits/DiffTable.tsx'
import { ResourceLabel } from '#src/components/queued-edits/ResourceLabel.tsx'
import { ResourceTypeIcon } from '#src/components/queued-edits/ResourceTypeIcon.tsx'
import { VerbGlyph } from '#src/components/queued-edits/VerbGlyph.tsx'

export interface ResourceDiffCardProps {
  change: ResourceChangeSet
  showTechnical?: boolean
  decision?: ResourceDecision
  // Who recorded the current decision and when — shown as an avatar beside the
  // approve/reject buttons, hover-revealing the name + decision time.
  decidedBy?: Reference<Practitioner>
  decidedAt?: string
  onApprove?: () => void
  onReject?: () => void
  defaultExpanded?: boolean
  // Merged into the root (tailwind-merge, caller wins). Lets a group section
  // recolor the left accent strip into a continuous group indicator bar.
  className?: string
  // The change's resolved expected owner (see useExpectedOwners) — the review
  // surface resolves owners in one pass (it also filters by them) and passes
  // each card its result. Distinct affordance from the decider: this is who
  // the change concerns, not who decided.
  expectedOwner?: Reference<Practitioner>
}

// The shared unit of consistency across all three surfaces (inbox / chat /
// modal). Renders flat and edge-to-edge — the list container owns the outer
// chrome; a left accent strip carries the approve/reject decision. Collapsed:
// one inline row — icon, resource name, change count, approve/reject +
// expand. Expanded: flat Field/Before/After table.
export function ResourceDiffCard({
  change,
  showTechnical = false,
  decision,
  decidedBy,
  decidedAt,
  onApprove,
  onReject,
  defaultExpanded = false,
  className,
  expectedOwner,
}: ResourceDiffCardProps): JSX.Element {
  const [expanded, setExpanded] = useState(defaultExpanded)
  const [techPanelOpen, setTechPanelOpen] = useState(false)

  const rows = flattenChanges(change.changes)
  const changedFieldCount = countChangedFields(change.changes)
  const hasActions = Boolean(onApprove || onReject)

  return (
    <div
      className={cn(
        'shrink-0 overflow-hidden border-border border-b border-l-2 bg-card transition-colors last:border-b-0',
        decision === 'approved' && 'border-l-pine-500',
        decision === 'rejected' && 'border-l-red-400',
        !decision && 'border-l-transparent',
        className
      )}
    >
      {/* Header row: verb · icon · name · change count · expand · actions —
          with the proposer's intent on a second line below. */}
      <div className="group flex flex-col gap-1 px-3 py-2">
        <div className="flex min-w-0 items-center gap-2.5">
          <VerbGlyph verb={change.verb} />
          <ResourceTypeIcon change={change} />
          {/* The label takes the flexible space and truncates; without
              flex-1+min-w-0 here a long title keeps its intrinsic width and
              shoves the decision/expand controls past the card edge. */}
          <div className="flex min-w-0 flex-1 items-center gap-2.5">
            <ResourceLabel change={change} />
            {showTechnical && change.subtitle && (
              <span className="min-w-0 truncate font-mono text-2xs text-muted-foreground">
                {change.subtitle}
              </span>
            )}
          </div>

          {/* Field-staleness drift: the record moved after the proposal was
              drafted, on a path this change touches — the proposer never saw
              the diff being shown. Flag it; the reviewer decides against the
              live diff either way. */}
          {change.changedSinceProposed && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge
                  className="shrink-0 whitespace-nowrap"
                  color="yellow"
                  variant="outline"
                >
                  <IconAlertTriangle className="size-3" />
                  Changed since proposed
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                This record was edited after the change was proposed. The
                before/after shown reflects the current version — review it
                closely.
              </TooltipContent>
            </Tooltip>
          )}

          {/* Read-only outcome (applied review): show the decision plus who
              made it inline, since there are no approve/reject controls. */}
          {!hasActions && decision && (
            <DecisionBadge
              decision={decision}
              decidedBy={decidedBy}
              decidedAt={decidedAt}
            />
          )}

          {hasActions && (
            <div className="flex shrink-0 items-center gap-1.5">
              {decision && decidedBy && (
                <DeciderAvatar decidedBy={decidedBy} decidedAt={decidedAt} />
              )}
              <Button
                variant={decision === 'rejected' ? 'destructive' : 'outline'}
                size="xs"
                onClick={onReject}
              >
                <IconX className="size-3.5" />
                Reject
              </Button>
              <Button
                variant={decision === 'approved' ? 'default' : 'outline'}
                size="xs"
                onClick={onApprove}
                className={cn(
                  decision === 'approved' &&
                    'bg-pine-600 text-white hover:bg-pine-700'
                )}
              >
                <IconCheck className="size-3.5" />
                Approve
              </Button>
            </div>
          )}
        </div>

        {/* Second line: left-aligned — the proposer's plain-English intent
            (sparkle ✦), or the raw field count when no intent was supplied —
            with the expand/collapse toggle pinned to the right. The toggle
            stays hidden until the row is hovered (or while expanded, so the
            open state is dismissable without hover). */}
        <div className="flex min-w-0 items-center gap-1">
          {change.intent ? (
            <p className="min-w-0 truncate text-xs text-muted-foreground">
              <IconSparkles className="mr-1 inline size-3 align-text-bottom" />
              {change.intent}
            </p>
          ) : (
            changedFieldCount > 0 && (
              <span className="text-xs text-muted-foreground">
                {changedFieldCount} change{changedFieldCount === 1 ? '' : 's'}
              </span>
            )
          )}
          <span className="flex-1" />
          {/* The change's expected owner, resolved live from current state
              (task assignee / appointment practitioner directly; orders and
              care plans defer to the internal ordering PCP or the IDT PCP). */}
          {expectedOwner && (
            <span className="flex min-w-0 shrink items-center gap-1.5 overflow-hidden">
              <span className="text-2xs tracking-wide text-muted-foreground/70 uppercase">
                Owner
              </span>
              <HoverPractitioner
                practitioner={expectedOwner}
                className="min-w-0 truncate text-xs text-muted-foreground"
              />
            </span>
          )}
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-label={expanded ? 'Collapse details' : 'Expand details'}
            className={cn(
              'shrink-0 rounded p-0.5 text-muted-foreground transition-opacity hover:bg-muted hover:text-foreground group-hover:opacity-100',
              expanded ? 'opacity-100' : 'opacity-0'
            )}
          >
            <IconChevronDown
              className={cn(
                'size-4 transition-transform',
                expanded && 'rotate-180'
              )}
            />
          </button>
        </div>
      </div>

      {/* Expanded diff table */}
      {expanded && (
        <div className="border-t border-border">
          {change.conversionError ? (
            <div className="flex items-start gap-2 px-4 py-3 text-xs text-amber-700">
              <IconAlertTriangle className="mt-0.5 size-3.5 shrink-0" />
              <span>
                Could not preview this change.
                {showTechnical && ` ${change.conversionError}`}
              </span>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <DiffTable
                rows={rows}
                showTechnical={showTechnical}
                showOriginal={Boolean(change.changedSinceProposed)}
              />
            </div>
          )}

          {showTechnical && change.patchOps && change.patchOps.length > 0 && (
            <div className="border-t border-border">
              <button
                type="button"
                onClick={() => setTechPanelOpen((v) => !v)}
                className="flex w-full items-center gap-2 px-4 py-2 text-left text-xs text-muted-foreground hover:text-foreground"
              >
                <IconCode className="size-3.5" />
                FHIRPath operations ({change.patchOps.length})
                <IconChevronDown
                  className={cn(
                    'size-3.5 transition-transform',
                    techPanelOpen && 'rotate-180'
                  )}
                />
              </button>
              {techPanelOpen && (
                <pre className="overflow-x-auto bg-slate-900 px-4 py-3 font-mono text-2xs text-slate-100">
                  {change.patchOps
                    .map(
                      (op) =>
                        `${op.op.padEnd(8)} ${op.fhirPath}${
                          op.value === undefined ? '' : ` = ${op.value}`
                        }`
                    )
                    .join('\n')}
                </pre>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// Read-only per-row decision summary for an applied review: the approve/reject
// outcome with the avatar + name of who made it (and when on hover).
function DecisionBadge({
  decision,
  decidedBy,
  decidedAt,
}: {
  decision: ResourceDecision
  decidedBy?: Reference<Practitioner>
  decidedAt?: string
}): JSX.Element {
  const approved = decision === 'approved'
  return (
    <div className="flex shrink-0 items-center gap-2">
      <span
        className={cn(
          'flex items-center',
          approved ? 'text-pine-700' : 'text-red-600'
        )}
      >
        {approved ? (
          <IconCheck className="size-4" />
        ) : (
          <IconX className="size-4" />
        )}
        <span className="sr-only">{approved ? 'Approved' : 'Rejected'}</span>
      </span>
      {decidedBy && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex">
              <Avatar profile={decidedBy} size={3.5} />
            </span>
          </TooltipTrigger>
          <TooltipContent>
            {referenceDisplay(decidedBy) ?? 'Unknown reviewer'}
            {decidedAt ? ` · ${formatDateTime(decidedAt)}` : ''}
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  )
}

// Avatar of whoever recorded the decision, hover-revealing their name and when
// they decided. Sits to the left of the approve/reject buttons (also used by
// the group header in QueuedEditsReview).
export function DeciderAvatar({
  decidedBy,
  decidedAt,
}: {
  decidedBy: Reference<Practitioner>
  decidedAt?: string
}): JSX.Element {
  const name = referenceDisplay(decidedBy) ?? 'Unknown reviewer'
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="flex shrink-0 items-center">
          <Avatar profile={decidedBy} size={6} />
        </span>
      </TooltipTrigger>
      <TooltipContent>
        {name}
        {decidedAt ? ` · ${formatDateTime(decidedAt)}` : ''}
      </TooltipContent>
    </Tooltip>
  )
}
